import React, { useRef, useEffect } from 'react';
import './StyleTest.css';
import KIWerkstattRegistrationForm from '../components/KIWerkstattRegistrationForm';

const StyleTest: React.FC = () => {
  const heroRef = useRef<HTMLElement | null>(null);
  const imageRef = useRef<HTMLImageElement | null>(null);

  // Floating animation only
  useEffect(() => {
    let animationId: number;
    let startTime = Date.now();

    const animate = () => {
      if (!imageRef.current) return;

      // Floating animation
      const elapsed = Date.now() - startTime;
      const floatCycle = (elapsed % 3000) / 3000; // 3 second cycle
      const floatY = Math.sin(floatCycle * Math.PI * 2) * 20; // 20px amplitude

      // Apply only floating transformation
      imageRef.current.style.transform = `translateY(${floatY}px)`;

      animationId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      if (animationId) {
        cancelAnimationFrame(animationId);
      }
    };
  }, []);

  // Ensure hero overlaps header on mobile exactly like page-header
  useEffect(() => {
    const applyMobileHeroOffset = () => {
      if (!heroRef.current) return;
      const isMobile = window.matchMedia('(max-width: 768px)').matches;
      if (!isMobile) {
        heroRef.current.style.marginTop = '';
        heroRef.current.style.paddingTop = '';
        return;
      }
      const header = document.querySelector<HTMLElement>('.header-container');
      const headerHeight = header ? header.offsetHeight : 70;
      heroRef.current.style.marginTop = `-${headerHeight}px`;
      // Keep existing vertical padding but add headerHeight to top
      // Default mobile top padding similar to page-header (2rem)
      const baseTopPaddingRem = 2;
      const baseTopPaddingPx = baseTopPaddingRem * 16; // assume 16px root
      const computedTopPadding = headerHeight + baseTopPaddingPx;
      heroRef.current.style.paddingTop = `${computedTopPadding}px`;
    };

    applyMobileHeroOffset();
    window.addEventListener('resize', applyMobileHeroOffset, { passive: true });
    window.addEventListener('orientationchange', applyMobileHeroOffset, { passive: true } as any);
    return () => {
      window.removeEventListener('resize', applyMobileHeroOffset as any);
      window.removeEventListener('orientationchange', applyMobileHeroOffset as any);
    };
  }, []);

  // Image Gallery Functionality
  useEffect(() => {
    let currentSlide = 0;
    const slides = document.querySelectorAll('.gallery-slide');
    const indicators = document.querySelectorAll('.indicator');
    
    if (slides.length === 0) return;

    const showSlide = (index: number) => {
      // Remove active class from all slides and indicators
      slides.forEach(slide => slide.classList.remove('active'));
      indicators.forEach(indicator => indicator.classList.remove('active'));
      
      // Add active class to current slide and indicator
      slides[index]?.classList.add('active');
      indicators[index]?.classList.add('active');
    };

    const nextSlide = () => {
      currentSlide = (currentSlide + 1) % slides.length;
      showSlide(currentSlide);
    };

    // Autoplay functionality
    const autoplayInterval = setInterval(nextSlide, 4000); // Change slide every 4 seconds

    // Click handlers for indicators
    const handleIndicatorClick = (e: Event) => {
      const target = e.target as HTMLElement;
      const slideIndex = parseInt(target.getAttribute('data-slide') || '0');
      currentSlide = slideIndex;
      showSlide(currentSlide);
      
      // Reset autoplay timer
      clearInterval(autoplayInterval);
      setTimeout(() => {
        const newAutoplayInterval = setInterval(nextSlide, 4000);
        return () => clearInterval(newAutoplayInterval);
      }, 4000);
    };

    indicators.forEach(indicator => {
      indicator.addEventListener('click', handleIndicatorClick);
    });

    // Cleanup
    return () => {
      clearInterval(autoplayInterval);
      indicators.forEach(indicator => {
        indicator.removeEventListener('click', handleIndicatorClick);
      });
    };
  }, []);

  return (
    <div className="page style-test-page" data-timestamp="2024-09-23-14:35">
      {/* Hero Section - Same structure as course detail page */}
      <section
        ref={(el) => { heroRef.current = el; }}
        className="page-header style-test-hero"
        data-section="style-test-hero"
      >
        <div className="style-test-hero-content">
          <div className="hero-text-column">
            <span className="hero-pretitle">Willkommen in der</span>
            <h1 className="style-test-hero-title">KI-Werkstatt</h1>
            <div className="hero-separator"></div>
            <p className="style-test-hero-subtitle">
              Der Ort für Deine KI-Projekte
            </p>
          </div>
          <div className="hero-image-column">
            <img 
              ref={(el) => { imageRef.current = el; }}
              src="/assets/test/linearttest.svg" 
              alt="KI Implementation Illustration"
              className="hero-image"
              onError={(e) => {
                console.error('Failed to load hero image:', e.currentTarget.src);
                e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
              }}
              onLoad={() => {
                console.log('Hero image loaded successfully');
              }}
            />
          </div>
        </div>
      </section>

      {/* Section 1: About */}
      <section className="style-test-section" data-section="style-test-section-1">
        <div className="container">
          <h2 className="section-title">Der <span className="highlight-text">KI-"Maker Space"</span> in Deiner Region</h2>
          <p className="section-subtitle">
            ENTWICKLE SINNVOLLE KI-ANWENDUNGSIDEEN, EXPERIMENTIERE MIT HARD- UND SOFTWARE, BAUE DEINE EIGENEN KI-LÖSUNGEN
          </p>
          <div className="style-test-grid">
            <div className="style-test-card">
              <div className="benefits-list">
                <div className="benefit-item">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Sicherer Raum zum Experimentieren</h4>
                    <p className="benefit-description">Teste modernste KI-Technologie in einer geschützten Umgebung außerhalb der Cloud.</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Zugang zu modernster KI-Technik</h4>
                    <p className="benefit-description">Erhalte Zugang zu leistungsfähiger Hard- und Software für Deine KI-Projekte.</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Zugang zu KI-KnowHow</h4>
                    <p className="benefit-description">Profitiere von unserer jahrelangen KI-Entwicklungserfahrung aus der Industrie.</p>
                  </div>
                </div>
                <div className="benefit-item">
                  <div className="benefit-content">
                    <h4 className="benefit-heading">Austausch mit anderen</h4>
                    <p className="benefit-description">Vernetzen Sie sich mit Gleichgesinnten und tauschen Sie Erfahrungen in einer aktiven Community aus.</p>
                  </div>
                </div>
              </div>
            </div>
            <div className="style-test-card">
              <div className="image-gallery">
                <div className="gallery-container">
                  <div className="gallery-slides">
                    <div className="gallery-slide active">
                      <img 
                        src="/assets/images/ki-werkstatt/IMG_0244.jpeg" 
                        alt="KI-Werkstatt Arbeitsplatz 1"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </div>
                    <div className="gallery-slide">
                      <img 
                        src="/assets/images/ki-werkstatt/IMG_0337.jpeg" 
                        alt="KI-Werkstatt Arbeitsplatz 2"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </div>
                    <div className="gallery-slide">
                      <img 
                        src="/assets/images/ki-werkstatt/IMG_0565.jpeg" 
                        alt="KI-Werkstatt Arbeitsplatz 3"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </div>
                    <div className="gallery-slide">
                      <img 
                        src="/assets/images/ki-werkstatt/IMG_1535.jpeg" 
                        alt="KI-Werkstatt Arbeitsplatz 4"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </div>
                    <div className="gallery-slide">
                      <img 
                        src="/assets/images/ki-werkstatt/IMG_3471.jpeg" 
                        alt="KI-Werkstatt Arbeitsplatz 5"
                        onError={(e) => {
                          e.currentTarget.src = '/assets/images/common/course-placeholder.svg';
                        }}
                      />
                    </div>
                  </div>
                  <div className="gallery-indicators">
                    <span className="indicator active" data-slide="0"></span>
                    <span className="indicator" data-slide="1"></span>
                    <span className="indicator" data-slide="2"></span>
                    <span className="indicator" data-slide="3"></span>
                    <span className="indicator" data-slide="4"></span>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Section 2: Reference Project */}
      <section className="style-test-section reference-project-section" data-section="style-test-section-2">
        <div className="container">
          <h2 className="section-title">Die <span className="highlight-text">KI-Werkstatt</span> bei der Firma Wöhler</h2>
          <p className="section-subtitle">SCHAUE DIR AN, WIE DIE FIRMA WÖHLER TECHNIK GMBH DIE KI-WERKSTATT ALS INNERBETRIEBLICHEN INNOVATIONSORT NUTZT</p>
          <div className="reference-project-content">
            <div className="video-container">
              <iframe
                src="https://www.youtube.com/embed/ZxLlVtJslnI"
                title="KI Werkstatt Referenzprojekt"
                frameBorder="0"
                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share"
                allowFullScreen
                className="youtube-video"
              />
            </div>
          </div>
        </div>
      </section>

      {/* Section 3: Next Workshop */}
      <section className="style-test-section" data-section="style-test-section-3">
        <div className="container">
          <h2 className="section-title">Nächste <span className="highlight-text">KI-Werkstatt</span> in Q1/2026 in Paderborn</h2>
          <p className="section-subtitle">MIT FOKUS AUF <span className="highlight-text">COMPUTER VISION</span> UND <span className="highlight-text">SCHÜTZBARKEIT VON KI-ERFINDUNGEN</span></p>
          
          <div className="workshop-grid">
            <div className="workshop-column">
              <h3>Ort</h3>
              <p><a href="https://www.last-drop.bar/" target="_blank" rel="noopener noreferrer" className="highlight-text">Last Drop - eSports & Gaming Bar</a><br />Marienpl. 7A, 33098 Paderborn</p>
            </div>
            <div className="workshop-column">
              <h3>Zeitraum</h3>
              <p>Datum wird mit Interessenten abgestimmt.<br />Uhrzeit: <span className="highlight-text">08:00 - 17:00 Uhr</span></p>
            </div>
            <div className="workshop-column">
              <h3>Praxisprojekt</h3>
              <p>Nachbau des KI-Sicherheitssystems <a href="https://www.youtube.com/watch?v=nIbTTiTfHn8" target="_blank" rel="noopener noreferrer" className="highlight-text">Handguard</a> der Firma Altendorf.</p>
            </div>
            <div className="workshop-column">
              <h3>Zielgruppe</h3>
              <p>KI-Interessierte, technische Fachkräfte und Entscheidungsträger.</p>
            </div>
            <div className="workshop-column">
              <h3>Voraussetzungen</h3>
              <p>Technisches Interesse.</p>
            </div>
            <div className="workshop-column">
              <h3>Teilnahmegebühr</h3>
              <p><span className="highlight-text">600€</span> zzgl. USt. pro Person inkl. Verpflegung und Arbeitsunterlagen.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 3 Duplicate: Next Workshop */}
      <section className="style-test-section" data-section="style-test-section-3-duplicate">
        <div className="container">
          <h2 className="section-title">Nächste <span className="highlight-text">KI-Werkstatt</span> in Q1/2026 in Salzkotten</h2>
          <p className="section-subtitle">MIT FOKUS AUF <span className="highlight-text">LOKAL BETRIEBENE KI-AGENTEN</span></p>
          
          <div className="workshop-grid">
            <div className="workshop-column">
              <h3>Ort</h3>
              <p><a href="https://hederlab.de/" target="_blank" rel="noopener noreferrer" className="highlight-text">heder:LAB</a><br />Lange Straße 25, 33154 Salzkotten</p>
            </div>
            <div className="workshop-column">
              <h3>Zeitraum</h3>
              <p>Datum wird mit Interessenten abgestimmt.<br />Uhrzeit: <span className="highlight-text">08:00 - 17:00 Uhr</span></p>
            </div>
            <div className="workshop-column">
              <h3>Praxisprojekt</h3>
              <p>Assistenzsystem für die Wartung von Anlagen- und Maschinen.</p>
            </div>
            <div className="workshop-column">
              <h3>Zielgruppe</h3>
              <p>KI-Interessierte, technische Fachkräfte und Entscheidungsträger.</p>
            </div>
            <div className="workshop-column">
              <h3>Voraussetzungen</h3>
              <p>Technisches Interesse.</p>
            </div>
            <div className="workshop-column">
              <h3>Teilnahmegebühr</h3>
              <p><span className="highlight-text">600€</span> zzgl. USt. pro Person inkl. Verpflegung und Arbeitsunterlagen.</p>
            </div>
          </div>
        </div>
      </section>

      {/* Section 4: Contact & Registration */}
      <section className="style-test-section" data-section="style-test-section-5">
        <div className="container">
          <h2 className="section-title"><span className="highlight-text">Unverbindliche</span> Registrierung</h2>
          <p className="section-subtitle">FÜR DIE NÄCHSTE KI-WERKSTATT IN IHRER REGION</p>
          <KIWerkstattRegistrationForm variant="verwaltung" />
        </div>
      </section>
    </div>
  );
};

export default StyleTest;
